import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { InterviewPortalInfo, InterviewBot } from '../../../../interfaces/interview'
import { getServicesManager } from '../../../../services'

// Types
interface SimplifiedBot {
  bot_id: string
  name: string
  description: string
  tags: string[]
}

export interface InterviewPortalAdminState {
  portals: InterviewPortalInfo[] | null
  availableBots: SimplifiedBot[]
  loading: boolean
  error: string | null
  creatingPortal: boolean
  updatingPortal: boolean
  deletingPortal: boolean
  selectedPortalId: string | null
}

const initialState: InterviewPortalAdminState = {
  portals: null,
  availableBots: [],
  loading: false,
  error: null,
  creatingPortal: false,
  updatingPortal: false,
  deletingPortal: false,
  selectedPortalId: null
}

// Async thunks
export const fetchInterviewPortals = createAsyncThunk(
  'interviewPortalAdmin/fetchPortals',
  async (_, { rejectWithValue }) => {
    try {
      const servicesManager = getServicesManager()
      const result = await servicesManager.getInterviewPortalLinks()
      
      if (result === null) {
        throw new Error('Failed to fetch interview portals')
      }
      
      return result
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error')
    }
  }
)

export const fetchAvailableBots = createAsyncThunk(
  'interviewPortalAdmin/fetchAvailableBots',
  async (_, { rejectWithValue }) => {
    try {
      const servicesManager = getServicesManager()
      const result = await servicesManager.getAllTeamBots()
      if (!result) {
        throw new Error('Failed to fetch available bots')
      }
      // Convert Bot[] to InterviewBot[] format
      return result.map((bot: any) => ({
        bot_id: bot.bot_id,
        name: bot.name,
        description: bot.description,
        tags: bot.tags || []
      }))
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error')
    }
  }
)

export const createInterviewPortal = createAsyncThunk(
  'interviewPortalAdmin/createPortal',
  async (portalData: {
    name: string
    description?: string
    password: string
    bot_ids: string[]
    max_calls_per_user: number
  }, { rejectWithValue }) => {
    try {
      const servicesManager = getServicesManager()
      const result = await servicesManager.createInterviewPortal(portalData)
      if (!result) {
        throw new Error('Failed to create interview portal')
      }
      return result
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error')
    }
  }
)

export const updateInterviewPortal = createAsyncThunk(
  'interviewPortalAdmin/updatePortal',
  async ({ portalId, updates }: {
    portalId: string
    updates: {
      name?: string
      description?: string
      password?: string
      bot_ids?: string[]
      max_calls_per_user?: number
    }
  }, { rejectWithValue }) => {
    try {
      const servicesManager = getServicesManager()
      const result = await servicesManager.updateInterviewPortal(portalId, updates)
      if (!result) {
        throw new Error('Failed to update interview portal')
      }
      // Return portalId and updates since the backend succeeded
      return { portalId, updates }
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error')
    }
  }
)

export const deleteInterviewPortal = createAsyncThunk(
  'interviewPortalAdmin/deletePortal',
  async (portalId: string, { rejectWithValue }) => {
    try {
      const servicesManager = getServicesManager()
      await servicesManager.deleteInterviewPortal(portalId)
      return portalId
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error')
    }
  }
)

// Slice
const interviewPortalAdminSlice = createSlice({
  name: 'interviewPortalAdmin',
  initialState,
  reducers: {
    setSelectedPortalId: (state, action: PayloadAction<string | null>) => {
      state.selectedPortalId = action.payload
    },
    clearError: (state) => {
      state.error = null
    },
    resetState: (state) => {
      Object.assign(state, initialState)
    }
  },
  extraReducers: (builder) => {
    // Fetch interview portals
    builder
      .addCase(fetchInterviewPortals.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchInterviewPortals.fulfilled, (state, action) => {
        state.loading = false
        state.portals = action.payload
      })
      .addCase(fetchInterviewPortals.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

    // Fetch available bots
    builder
      .addCase(fetchAvailableBots.pending, (state) => {
        state.error = null
      })
      .addCase(fetchAvailableBots.fulfilled, (state, action) => {
        state.availableBots = action.payload
      })
      .addCase(fetchAvailableBots.rejected, (state, action) => {
        state.error = action.payload as string
      })

    // Create interview portal
    builder
      .addCase(createInterviewPortal.pending, (state) => {
        state.creatingPortal = true
        state.error = null
      })
      .addCase(createInterviewPortal.fulfilled, (state, action) => {
        state.creatingPortal = false
        if (state.portals) {
          state.portals.push(action.payload)
        } else {
          state.portals = [action.payload]
        }
      })
      .addCase(createInterviewPortal.rejected, (state, action) => {
        state.creatingPortal = false
        state.error = action.payload as string
      })

    // Update interview portal
    builder
      .addCase(updateInterviewPortal.pending, (state) => {
        state.updatingPortal = true
        state.error = null
      })
      .addCase(updateInterviewPortal.fulfilled, (state, action) => {
        state.updatingPortal = false
        if (state.portals) {
          const index = state.portals.findIndex(p => p.portal_id === action.payload.portalId)
          if (index !== -1) {
            // Merge updates with existing portal data instead of replacing it
            state.portals[index] = { ...state.portals[index], ...action.payload.updates }
          }
        }
      })
      .addCase(updateInterviewPortal.rejected, (state, action) => {
        state.updatingPortal = false
        state.error = action.payload as string
      })

    // Delete interview portal
    builder
      .addCase(deleteInterviewPortal.pending, (state) => {
        state.deletingPortal = true
        state.error = null
      })
      .addCase(deleteInterviewPortal.fulfilled, (state, action) => {
        state.deletingPortal = false
        if (state.portals) {
          state.portals = state.portals.filter(p => p.portal_id !== action.payload)
        }
        if (state.selectedPortalId === action.payload) {
          state.selectedPortalId = null
        }
      })
      .addCase(deleteInterviewPortal.rejected, (state, action) => {
        state.deletingPortal = false
        state.error = action.payload as string
      })
  }
})

export const { setSelectedPortalId, clearError, resetState } = interviewPortalAdminSlice.actions
export default interviewPortalAdminSlice.reducer